<!--
	Checkbox rows for one legend layer, plus a Select all/none toggle and info button.
-->
<script lang="ts">
	import type { Snippet } from 'svelte';
	import { PATCH_FALLBACK } from '#lib/vizPalette.js';
	import LegendPatch from '#lib/components/LegendPatch.svelte';
	import LegendRow from '#lib/components/LegendRow.svelte';
	import InfoButton from '#lib/components/InfoButton.svelte';
	import AbleyButton from '#lib/components/AbleyButton.svelte';
	import {
		legendStore,
		type MapLayer,
		type MapLegendItem
	} from '#lib/stores/legendStore.svelte.js';
	import { geodataStore } from '#lib/stores/geodataStore.svelte.js';

	interface Props {
		legend: MapLayer;
		/** Patch shape, or a snippet rendering each item's own swatch (e.g. a LegendIcon). */
		patch: 'round' | 'square' | Snippet<[MapLegendItem]>;
		disabled?: boolean;
		/** Display label overrides keyed by the raw legend label. */
		labels?: Record<string, string>;
		selectAll?: boolean;
		/** Replaces the default single-item visibility toggle. */
		ontoggle?: (legendItemId: string, enabled: boolean) => void;
		/** Replaces the default Select all/none behaviour. */
		ontoggleall?: (enabled: boolean) => void;
	}

	const {
		legend,
		patch,
		disabled = false,
		labels = {},
		selectAll = true,
		ontoggle = (legendItemId, enabled) =>
			legendStore.setLegendLayerVisibility({ layerId: legend.layerId, legendItemId, enabled }),
		ontoggleall = (enabled) =>
			legendStore.setAllLegendLayerVisibility({ layerId: legend.layerId, enabled })
	}: Props = $props();

	const infoText = $derived(geodataStore.layerAbstract(legend.systemId));
</script>

{#if disabled}
	<p class="hint">Zoom in to enable these layers</p>
{/if}
{#each legend.legendItems as item (item.legendItemId)}
	<div class="legendRowBox">
		<LegendRow
			label={labels[item.label] ?? item.label}
			checked={item.enabled}
			{disabled}
			disabledTooltip="Zoom in to enable this layer"
			onchange={() => ontoggle(item.legendItemId, !item.enabled)}>
			{#if typeof patch === 'function'}
				{@render patch(item)}
			{:else}
				<LegendPatch color={item.color ?? PATCH_FALLBACK} shape={patch} />
			{/if}
		</LegendRow>
	</div>
{/each}
<div class="actionsBox">
	{#if selectAll}
		<AbleyButton
			variant="text"
			size="xs"
			{disabled}
			onclick={() => ontoggleall(!legend.layerAllEnabled)}>
			{legend.layerAllEnabled ? 'Select none' : 'Select all'}
		</AbleyButton>
	{/if}
	{#if infoText}
		<div class="info">
			<InfoButton content={infoText} />
		</div>
	{/if}
</div>

<style>
	.actionsBox {
		display: flex;
		align-items: center;
		gap: var(--space-1);
	}

	.info {
		margin-inline-start: auto;
	}
</style>
